<template>
  <div
    v-if="open"
    class="overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="hint-dialog-title"
    @keydown.esc.stop.prevent="onClose"
  >
    <div
      ref="dialogEl"
      class="dialog"
      tabindex="-1"
    >
      <header class="dialog-header">
        <h3
          id="hint-dialog-title"
          class="h2"
        >
          Hinweis
        </h3>
        <button
          class="btn-close"
          type="button"
          aria-label="Schließen"
          @click="onClose"
        />
      </header>

      <div class="dialog-body">
        <div class="meta">
          <div class="meta-row">
            <span class="label">Schüler</span>
            <span class="value">{{ studentName }}</span>
          </div>

          <div
            v-if="validFor"
            class="meta-row"
          >
            <span class="label">gültig für</span>
            <span class="value">{{ formatDateDdMmYyyy(validFor) }}</span>
          </div>

          <div class="meta-row">
            <span class="label">Absender</span>
            <span class="value">{{ senderName }}</span>
          </div>

          <div
            v-if="dateVal"
            class="meta-row"
          >
            <span class="label">Datum</span>
            <span class="value">{{ formatDateDdMmYyyy(dateVal) }}</span>
          </div>

          <div
            v-if="sentAt"
            class="meta-row"
          >
            <span class="label">Gesendet am</span>
            <span class="value">{{ formatDateTime(sentAt) }}</span>
          </div>
        </div>

        <div
          v-if="message"
          class="message-box"
        >
          <p class="message pre-wrap">
            {{ message }}
          </p>
        </div>
      </div>

      <footer class="dialog-footer">
        <button
          class="btn"
          type="button"
          :disabled="markLoading"
          :style="{'margin-right': (isTeacherOrAdmin) ? 'auto' :''}"
          @click="onClose"
        >
          Schließen
        </button>

        <button
          v-if="isTeacherOrAdmin"
          class="btn btn-primary"
          type="button"
          :disabled="notificationCreated || markLoading || !hintId || !studentId"
          @click="onOpenCreateNotification"
        >
          {{ notificationCreated ? 'Meldung erzeugt…' : 'Meldung erzeugen' }}
        </button>

        <button
          v-if="isTeacherOrAdmin"
          class="btn btn-primary"
          type="button"
          :disabled="isRead || markLoading || !hintId"
          @click="onMarkRead"
        >
          {{ isRead ? 'Gelesen' : (markLoading ? 'Markiere…' : 'Als gelesen markieren') }}
        </button>
      </footer>
    </div>

    <NotificationCreateDialog
      :open="createOpen"
      :prefill-student-id="studentId"
      :prefill-kind="'daily'"
      :prefill-date-from="prefillDate"
      :prefill-date-to="prefillDate"
      @close="createOpen = false"
      @created="onNotificationCreated"
    />
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import {hintsApi} from '@/api/hints';
import teacherInfosApi from '@/api/teacherInfos';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';
import {useAuthStore} from "@/stores/auth";

const auth = useAuthStore();
const isTeacherOrAdmin = computed(() => auth.isAdmin === true || auth.isTeacher === true);
const props = defineProps({
  open: {type: Boolean, default: false},
  hint: {type: Object, default: () => ({})}
});
const emit = defineEmits(['close', 'marked-read', 'notification-created']);

const dialogEl = ref(null);
const markLoading = ref(false);
const createOpen = ref(false);

const hintId = computed(() => props.hint?.id ?? null);

const studentId = computed(() => {
  const raw =
      props.hint?.studentId ??
      props.hint?.student_id ??
      props.hint?.student?.id ??
      null;
  const n = raw === null || raw === undefined ? null : Number(raw);
  return Number.isFinite(n) ? n : null;
});

const studentName = computed(() =>
    props.hint?.student_name ||
    props.hint?.studentName ||
    props.hint?.student?.name ||
    '—'
);

const senderName = computed(() =>
    props.hint?.senderName ||
    '—'
);

const message = computed(() =>
    props.hint?.message ||
    props.hint?.text ||
    props.hint?.body ||
    ''
);

const dateVal = computed(() =>
    props.hint?.date ||
    props.hint?.for_date ||
    props.hint?.date_from ||
    props.hint?.dateFrom ||
    null
);

const validFor = computed(() =>
    props.hint?.validOn ||
    props.hint?.valid_on ||
    props.hint?.for_date ||
    null
);

const sentAt = computed(() =>
    props.hint?.sent_at ||
    props.hint?.created_at ||
    props.hint?.createdAt ||
    null
);

const isRead = computed(() =>
    !!(props.hint?.readAt || props.hint?.read_at)
);

const notificationCreatedLocal = ref(false);
const notificationCreated = computed(() =>
    notificationCreatedLocal.value || !!(props.hint?.notificationCreated || props.hint?.notification_created)
);

const prefillDate = computed(() => {
  const v = validFor.value || null;
  if (!v) return null;
  // NotificationCreateDialog erwartet i. d. R. 'YYYY-MM-DD'
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? s : null;
});

watch(
    () => props.open,
    (o) => {
      if (o) {
        notificationCreatedLocal.value = false;
        createOpen.value = false;
      }
    }
);

function formatDateDdMmYyyy(iso) {
  if (!iso) return '';
  try {
    const d = new Date(String(iso));
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${dd}.${mm}.${yyyy}`;
  } catch {
    return String(iso);
  }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(String(iso));
    return d.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return String(iso);
  }
}

function onOpenCreateNotification() {
  if (notificationCreated.value) return;
  createOpen.value = true;
}

async function onNotificationCreated() {
  // Nach dem Speichern der Meldung:
  // 1) Flag setzen
  // 2) Hinweis als gelesen markieren
  if (!hintId.value) {
    createOpen.value = false;
    return;
  }

  markLoading.value = true;
  try {
    await teacherInfosApi.markNotificationCreated(hintId.value);

    if (!isRead.value) {
      await hintsApi.markRead(hintId.value);
      emit('marked-read', hintId.value);
    }

    notificationCreatedLocal.value = true;
    emit('notification-created', hintId.value);

    createOpen.value = false;
    emit('close');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Create-notification follow-up failed', e);
  } finally {
    markLoading.value = false;
  }
}

async function onMarkRead() {
  if (!hintId.value || isRead.value) return;
  markLoading.value = true;
  try {
    await hintsApi.markRead(hintId.value);
    emit('marked-read', hintId.value);
    emit('close');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Mark read failed', e);
  } finally {
    markLoading.value = false;
  }
}

function onClose() {
  emit('close');
}

function trapFocus(e) {
  if (!props.open) return;
  if (e.key !== 'Tab') return;

  const focusable = dialogEl.value?.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])');
  if (!focusable || focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

watch(() => props.open, (v) => {
  if (v) {
    setTimeout(() => {
      dialogEl.value?.focus();
    }, 0);
  }
});

onMounted(() => {
  window.addEventListener('keydown', trapFocus);
});
onUnmounted(() => {
  window.removeEventListener('keydown', trapFocus);
});
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.dialog {
  background: var(--surface);
  color: var(--text);
  width: min(640px, calc(100% - 24px));
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
  padding: 12px 12px 10px;
  outline: none;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

.btn-close {
  width: 28px;
  height: 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  cursor: pointer;
  position: relative;
}

.btn-close::before,
.btn-close::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 12px;
  height: 2px;
  background: var(--muted);
  transform-origin: center;
}

.btn-close::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.btn-close::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.btn-close:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}

.dialog-body {
  display: grid;
  gap: 10px;
}

.row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  align-items: start;
}

.label {
  color: var(--muted);
  font-size: 12px;
  line-height: 16px;
}

.value {
  font-size: 14px;
  line-height: 20px;
}

.pre-wrap {
  white-space: pre-wrap;
}

.dialog-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

/* Meta-Block und Nachricht analog zur Readonly-Ansicht in TeacherInfos.vue */
.meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
  margin-bottom: 12px;
}

.meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  align-items: start;
}

.message-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.message {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}
</style>
